import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {doc, getDoc, setDoc} from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, {defaultSchema} from 'rehype-sanitize';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ResponsiveDialog from '-/components/ResponsiveDialog';
import {UserContext} from '-/contexts/User';
import {db} from '-/firebase';
import {isHiddenAccount} from '@embertracking/common';
import {hasPermission, getApiBase} from '-/data/utils';
import {reportError} from '-/data/errorReporting';

const sanitizeSchema = {
    ...defaultSchema,
    protocols: {
        ...defaultSchema.protocols,
        src: ['http', 'https']
    }
};

export default function WhatsNewDialog() {
    const {currentUser} = useContext(UserContext);
    const navigate = useNavigate();
    const uid = currentUser?.uid;

    const [latest, setLatest] = useState(null);
    const [open, setOpen] = useState(false);

    // Not for hidden accounts (the operator, a hub user signed in from the hub,
    // #2307): the notes are for this department's own admins, and a district
    // user moving between departments would otherwise dismiss them in each one.
    const canReceive = useMemo(
        () => Boolean(currentUser) && !currentUser.isStation && !isHiddenAccount(currentUser) && hasPermission(currentUser, 'admin'),
        [currentUser]
    );

    useEffect(() => {
        if (!canReceive || !uid) return;

        const base = getApiBase();
        if (!base) return;

        let cancelled = false;

        (async () => {
            try {
                const [notesRes, prefsSnap] = await Promise.all([
                    axios.get(`${base}/releaseNotes`, {timeout: 60000}),
                    getDoc(doc(db, 'userPreferences', uid))
                ]);

                if (cancelled) return;

                const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
                if (!notes.length) return;

                // Skip notes flagged noUserVisibleChanges — the release still
                // shows on /settings/changelog, but the auto-popup is reserved
                // for releases that actually change what admins see.
                const newest = notes.find(n => !n.noUserVisibleChanges);
                if (!newest) return;

                const seen = prefsSnap.exists() ? (prefsSnap.data().whatsnewSeenVersion || 0) : 0;

                setLatest(newest);
                if (newest.version > seen) setOpen(true);
            } catch (e) {
                // A permission-denied here is an expected auth-transition race —
                // the read fires while the session is mid-transition (confirmed
                // repro: first sign-in on a new device, where the getDoc goes out
                // before the fresh ID token propagates to the Firestore stream;
                // sign-out/token expiry hits the same window). The userPreferences
                // rule only requires an authenticated session for your own uid,
                // so it can't be a real authorization failure.
                //
                // An axios error with no response is a transport failure —
                // timeout, offline, a stalled connection on a backgrounded
                // mobile tab (JRFD-LOG-1E: 60s timeout on iOS while the
                // endpoint's p95 is ~2s). callFunction treats these as
                // expected too; the popup simply tries again next load.
                const isTransportFailure = e?.isAxiosError === true && !e.response;
                if (e?.code !== 'permission-denied' && !isTransportFailure) {
                    reportError(e, {context: 'WhatsNewDialog.load', component: 'WhatsNewDialog'});
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canReceive, uid]);

    const dismiss = useCallback(async () => {
        setOpen(false);
        if (!uid || !latest) return;

        try {
            const ref = doc(db, 'userPreferences', uid);
            await setDoc(ref, {whatsnewSeenVersion: latest.version}, {merge: true});
        } catch (e) {
            // Same auth-transition race as load — see above.
            if (e?.code !== 'permission-denied') {
                reportError(e, {context: 'WhatsNewDialog.dismiss', component: 'WhatsNewDialog'});
            }
        }
    }, [latest, uid]);

    const viewAll = useCallback(async () => {
        await dismiss();
        navigate('/settings/changelog');
    }, [dismiss, navigate]);

    if (!latest || !open) return null;

    const formattedDate = latest.date
        ? moment(latest.date).format('MMMM D, YYYY')
        : 'Latest release';

    return (
        <ResponsiveDialog
            open={open}
            onClose={dismiss}
            maxWidth="sm"
            data-testid="whats-new-dialog"
        >
            <DialogTitle>
                <Stack spacing={0.5}>
                    <Typography variant="h6" component="span">What&apos;s new &mdash; {formattedDate}</Typography>
                    {latest.title && !/^Release v\d+$/i.test(latest.title) && (
                        <Typography variant="body2" sx={{color: 'text.secondary'}}>{latest.title}</Typography>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    '& img': {maxWidth: '100%', height: 'auto', borderRadius: 1, display: 'block', my: 1},
                    '& pre': {overflow: 'auto'},
                    '& a': {color: 'primary.main'}
                }}
            >
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
                    urlTransform={url => {
                        if (!url) return url;
                        if (/^(https?:|\/)/.test(url)) return url;
                        return '';
                    }}
                >
                    {latest.bodyMarkdown || ''}
                </ReactMarkdown>
            </DialogContent>
            <DialogActions>
                <Button onClick={viewAll} data-testid="whats-new-view-all">View all release notes</Button>
                <Button onClick={dismiss} variant="contained" data-testid="whats-new-dismiss">Got it</Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}

// Exported for unit tests.
export {sanitizeSchema};
